import React, { useEffect, useState, useCallback } from 'react';
import { Button, Card, Col, Divider, Form, Input, List, Row, Spin, Switch, Upload, message } from 'antd';
import {
	TagsOutlined,
	UserAddOutlined,
	KeyOutlined,
	UserOutlined,
	ToolOutlined,
	UploadOutlined
} from '@ant-design/icons';
import Icon from 'components/util-components/Icon';
import Flex from 'components/shared-components/Flex'
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import MaintenanceConfig from './MaintenanceConfig';
import { ROW_GUTTER } from 'constants/ThemeConstant';

const AdminSetting = () => {
	const { api, authToken, adminConfig, setMaintenance } = useMyContext();
	const [smsApiKey, setSmsApiKey] = useState(adminConfig?.sms_apiKey);
	const [smsApiSenderId, setSmsApiSenderId] = useState(adminConfig?.sms_senderId);
	const [loading, setLoading] = useState(false);
	const [config, setConfig] = useState([]);
	const [images, setImages] = useState({
		bg: null,
		logo: null
	});



	const GetSettingData = useCallback(async () => {
		setLoading(true)
		try {
			const response = await apiClient.get(`${api}setting`,
				{
					headers: {
						'Authorization': 'Bearer ' + authToken,
					}
				});
			const data = response.data.setting;
			setSmsApiKey(data?.sms_apiKey);
			setSmsApiSenderId(data?.sms_senderId);
			setMaintenance(data?.maintenance === 1)
			setImages({
				bg: data?.login_bg || null,
				logo: data?.logo || null
			});
			setConfig([
				{
					key: 'logs',
					title: 'Logs',
					icon: TagsOutlined,
					desc: 'You will receive a log when a new request comes in webhook.',
					allow: data?.logs === 1 ? true : false
				},
				{
					key: 'maintenance',
					title: 'Maintenance',
					icon: ToolOutlined,
					desc: 'Puts the application into maintenance mode, temporarily disabling user access.',
					allow: data?.maintenance && data?.maintenance === 1 ? true : false
				},
				{
					key: 'impersonate_otp',
					title: 'Impersonate OTP',
					icon: KeyOutlined, // You can change the icon as necessary
					desc: 'Require OTP for login when impersonating another user.',
					allow: data?.impersonate_otp && data?.impersonate_otp === 1 ? true : false
				},
				{
					key: 'allow_register_user',
					title: 'Allow Register User',
					icon: UserAddOutlined,
					desc: 'Allow anybody to create account manually.',
					allow: data?.allow_register_user === 1 ? true : false
				}
			]);
		} catch (error) {
			// console.log(error);
		} finally {
			setLoading(false)
		}
	}, [api, authToken, setMaintenance])

	useEffect(() => {
		GetSettingData()
	}, [GetSettingData])

	const handleSwitchChange = (key, checked) => {
		// console.log(key)
		// console.log(checked)
		const updatedConfig = config.map(item => {
			if (item.key === key) {
				UpdateSetting(key, checked ? true : false)
				return { ...item, allow: checked };
			}
			return item;
		});
		setConfig(updatedConfig);
	};

	const UpdateSetting = async (key, value) => {
		try {
			const formData = new FormData();
			formData.append(key, value);

			await apiClient.post(`${api}update-setting`, formData,
				{
					headers: {
						'Authorization': 'Bearer ' + authToken,
						'Content-Type': 'multipart/form-data',
					}
				});
		} catch (error) {
		}
	}

	const handleUpdateSmsConfig = async (values) => {
		//	console.log('Received values from form: ', values);
		try {
			const response = await apiClient.post(`${api}update-sms-config`, {
				sms_apiKey: values.apiKey,
				sms_senderId: values.senderId
			},
				{
					headers: {
						'Authorization': 'Bearer ' + authToken,
					}
				});
			const data = response.data.message;
			message.success(data);
		} catch (error) {
		}
	};

	const validateImageBeforeUpload = (file) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
		if (!isJpgOrPng) {
			message.error('You can only upload JPG/PNG file!');
			return false;
		}
		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isLt2M) {
			message.error('Image must smaller than 2MB!');
			return false;
		}
		return false; // Prevent default upload but allow file selection
	};

	const handleImageChange = (type) => (info) => {
		const { fileList } = info;

		if (fileList.length > 0) {
			const file = fileList[0];
			if (file.originFileObj) {
				// Store the actual file object for upload
				setImages(prev => ({
					...prev,
					[type]: file.originFileObj
				}));
			}
		} else {
			// File was removed
			setImages(prev => ({
				...prev,
				[type]: null
			}));
		}
	};

	const handleUpload = async (type) => {
		const file = images[type];
		if (!file) {
			message.error('Please upload an image file first!');
			return;
		}

		console.log(`Uploading ${type} file:`, file);
		const uploadName = type === 'bg' ? 'login_bg' : 'logo';
		try {
			await UpdateSetting(uploadName, file);
			message.success(`${type === 'bg' ? 'Background image' : 'Logo'} uploaded successfully!`);
			// Refresh the settings after upload
			GetSettingData();
		} catch (error) {
			message.error(`${type === 'bg' ? 'Background image' : 'Logo'} upload failed!`);
		}
	};


	return (
		<Card>
			{loading ? <div className="d-flex justify-content-center"><Spin /></div> :
				<>
					<h2 className="mb-4">Setting</h2>
					<List
						itemLayout="horizontal"
						dataSource={config}
						renderItem={item => (
							<List.Item>
								<Flex justifyContent="space-between" alignItems="center" className="w-100">
									<div className="d-flex align-items-center">
										<Icon className="h1 mb-0 text-primary" type={item.icon} />
										<div className="ml-3">
											<h4 className="mb-0">{item.title}</h4>
											<p>{item.desc}</p>
										</div>
									</div>
									<div className="ml-3">
										<Switch defaultChecked={item?.allow}
											onChange={checked => handleSwitchChange(item?.key, checked)}
										/>
									</div>
								</Flex>
							</List.Item>
						)}
					/>

					<Divider />
					<Row gutter={ROW_GUTTER}>
						<Col span="24">
							<h4 className="mb-3">{'SMS Configuration'}</h4>
							<Form
								name="customized_form_controls"
								layout="inline"
								onFinish={handleUpdateSmsConfig}
							>

								<Form.Item label={'API Key'} name={'apiKey'} initialValue={smsApiKey}>
									<Input
										type="text"
										prefix={<KeyOutlined className="site-form-item-icon" />}
										value={smsApiKey}
										onChange={(e) => setSmsApiKey(e.target.value)}
										placeholder='ApiKey'
									/>
								</Form.Item>
								<Form.Item label={'Sender ID'} name={'senderId'} initialValue={smsApiSenderId}>
									<Input
										type="text"
										prefix={<UserOutlined className="site-form-item-icon" />}
										value={smsApiSenderId}
										onChange={(e) => setSmsApiSenderId(e.target.value)}
										placeholder='Sender ID'
									/>

								</Form.Item>
								<Form.Item>
									<Button type="primary" htmlType="submit">
										Submit
									</Button>
								</Form.Item>
							</Form>
						</Col>
						<Divider />
						<MaintenanceConfig />
					</Row>
					<Divider />
					<div className="d-flex">
						<div className="bg">
							<h4 className="mb-3">{'Login Background Image'}</h4>
							<Upload
								listType="picture-card"
								maxCount={1}
								beforeUpload={validateImageBeforeUpload}
								onChange={handleImageChange('bg')}
								accept="image/*"
								onRemove={() => {
									setImages(prev => ({ ...prev, bg: null }));
									return true;
								}}
								fileList={images.bg ? [{
									uid: '-1',
									name: 'background',
									status: 'done',
									url: typeof images.bg === 'string'
										? images.bg
										: URL.createObjectURL(images.bg),
								}] : []}
								customRequest={({ onSuccess }) => {
									// Prevent default upload, just mark as success
									onSuccess("ok");
								}}
							>
								{!images.bg && (
									<div>
										<div>+ Upload</div>
									</div>
								)}
							</Upload>
							<Button
								type="primary"
								icon={<UploadOutlined />}
								onClick={() => handleUpload('bg')}
								style={{ marginTop: 16 }}
							>
								Upload Background Image
							</Button>
						</div>
						<div className="logo">
							<h4 className="mb-3">{'Logo'}</h4>
							<Upload
								listType="picture-card"
								maxCount={1}
								beforeUpload={validateImageBeforeUpload}
								onChange={handleImageChange('logo')}
								accept="image/*"
								onRemove={() => {
									setImages(prev => ({ ...prev, logo: null }));
									return true;
								}}
								fileList={images.logo ? [{
									uid: '-2',
									name: 'logo',
									status: 'done',
									url: typeof images.logo === 'string'
										? images.logo
										: URL.createObjectURL(images.logo),
								}] : []}
								customRequest={({ onSuccess }) => {
									// Prevent default upload, just mark as success
									onSuccess("ok");
								}}
							>
								{!images.logo && (
									<div>
										<div>+ Upload</div>
									</div>
								)}
							</Upload>
							<Button
								type="primary"
								icon={<UploadOutlined />}
								onClick={() => handleUpload('logo')}
								style={{ marginTop: 16 }}
							>
								Upload Logo
							</Button>
						</div>
					</div>
				</>
			}
		</Card>
	)
}

export default AdminSetting;
